import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Active-tenant state for the SPA.
//
// Persisted to localStorage so a refresh keeps you in the workspace you
// last opened (matters for platform admins who routinely jump between
// tenants). The API still resolves a fallback if we send a tenantId the
// caller doesn't have access to, so a stale value here is recoverable —
// the worst case is one redirect.
//
// We deliberately keep this independent of `useMe()`: the `Me` payload
// carries an `activeTenantId` that the server picked, but the SPA's
// choice (OrgSwitcher click) needs to win. If localStorage is unset,
// we let the API choose by sending no `X-Tenant-Id` header at all.

const STORAGE_KEY = "dmarc.activeTenantId";

interface TenantContextValue {
  activeTenantId: number | null;
  setActiveTenantId: (id: number | null) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function readStored(): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [activeTenantId, setState] = useState<number | null>(() => readStored());

  // Sync across tabs — if another tab switches workspace we don't want
  // this one to keep firing requests at the old tenant.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setState(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setActiveTenantId = useCallback((id: number | null) => {
    if (typeof window !== "undefined") {
      if (id == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, String(id));
    }
    setState(id);
  }, []);

  const value = useMemo(
    () => ({ activeTenantId, setActiveTenantId }),
    [activeTenantId, setActiveTenantId],
  );
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useActiveTenantId(): number | null {
  const ctx = useContext(TenantContext);
  // Tolerate a missing provider so non-app surfaces (tests, storybook)
  // don't have to wire it up — they get the "server chooses" behavior.
  return ctx?.activeTenantId ?? null;
}

export function useSetActiveTenantId(): (id: number | null) => void {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    return () => {
      throw new Error("useSetActiveTenantId called outside <TenantProvider>");
    };
  }
  return ctx.setActiveTenantId;
}
